<script setup lang="ts">
import { ref, computed, useTemplateRef, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { UiAside, UiText, UiButton, UiIcon, UiDialog, UiSegmentedControl, UiTabs, UiTabsItem, useToaster } from "@shopexperts/shared";
import {
  DraftLeadCard,
  DraftLeadForm,
  LeadForm,
  AssignExpertsForm,
  useDeleteLead,
  useMarkLeadResolved,
} from "@shopexperts/admin-leads-manager";
import type { AdminLeadQueueRow, DraftLeadAdminResponse, LeadAdminResponse } from "@shopexperts/legacy-api";
import { useAdminLeadQueue } from "@/app/composables/admin-projects/useAdminLeadQueue";
import { useAdminLeadDetail } from "@/app/composables/admin-projects/useAdminLeadDetail";
import { useAdminDraftLeads } from "@/app/composables/draft-leads/useAdminDraftLeads";
import { useSubmitDraftLead } from "@/app/composables/draft-leads/useSubmitDraftLead";
import { useDeleteDraftLead } from "@/app/composables/draft-leads/useDeleteDraftLead";
import AdminLeadProjectCard from "@/app/components/admin-projects/AdminLeadProjectCard.vue";
import { useQueryClient } from "@tanstack/vue-query";

const route = useRoute();
const router = useRouter();
const toaster = useToaster();

type TabValue = "pending" | "all" | "attention" | "draft-pending" | "draft-all";
const VALID_TABS: TabValue[] = ["pending", "all", "attention", "draft-pending", "draft-all"];
type AttentionScope = "recent" | "all";
type AttentionReason = "client" | "expert";
const ATTENTION_REASON_OPTIONS = [
  { label: "Waiting on client", value: "client" },
  { label: "Waiting on expert", value: "expert" },
] as const;
const ATTENTION_SCOPE_OPTIONS = [
  { label: "Slack alert window", value: "recent" },
  { label: "All waiting", value: "all" },
] as const;
const ATTENTION_DESCRIPTIONS: Record<AttentionReason, { intro: string; empty: string }> = {
  client: {
    intro: "Projects where the expert sent the last message and the client hasn't replied.",
    empty: "No project is waiting on a client reply in this range",
  },
  expert: {
    intro: "Newly assigned projects where the expert hasn't sent a message yet.",
    empty: "No newly assigned project is waiting on its expert in this range",
  },
};
const SCROLL_STORAGE_KEY = "admin-leads-projects:scrollTop";

// Hydrate active tab from URL query so navigating back from the project
// detail page (via the in-app back button OR the browser back button)
// restores the previously selected tab.
function readInitialTab(): TabValue {
  const v = route.query.tab;
  if (typeof v === "string" && (VALID_TABS as string[]).includes(v)) {
    return v as TabValue;
  }
  return "pending";
}

const activeTab = ref<TabValue>(readInitialTab());
const attentionScope = ref<AttentionScope>(route.query.scope === "all" ? "all" : "recent");
const attentionReason = ref<AttentionReason>(route.query.reason === "expert" ? "expert" : "client");
const scrollContainer = useTemplateRef<HTMLElement>("scrollContainer");

// Persist active tab in the URL using router.replace so we don't add a
// history entry on every tab click but the URL still describes the state.
watch(activeTab, (next) => {
  const current = typeof route.query.tab === "string" ? route.query.tab : "";
  if (current === next) return;
  const nextQuery: Record<string, string> = { ...route.query as Record<string, string>, tab: next };
  // Default tab is "pending" — keep the URL clean for that case.
  if (next === "pending") delete nextQuery.tab;
  if (next !== "attention") {
    delete nextQuery.scope;
    delete nextQuery.reason;
  }
  router.replace({ query: nextQuery });
});

watch([attentionScope, attentionReason], ([scope, reason]) => {
  if (activeTab.value !== "attention") return;
  const nextQuery: Record<string, string> = { ...route.query as Record<string, string> };
  if (scope === "all") nextQuery.scope = "all";
  else delete nextQuery.scope;
  if (reason === "expert") nextQuery.reason = "expert";
  else delete nextQuery.reason;
  router.replace({ query: nextQuery });
});

// Save scroll position whenever the user leaves this route so we can
// restore it on the way back (browser back, in-app back, or any other
// route change that brings them back here).
onBeforeRouteLeave(() => {
  if (!scrollContainer.value) return;
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(scrollContainer.value.scrollTop));
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — ignore.
  }
});

const queueStatus = computed<"pending" | "matched" | "">(() => {
  return activeTab.value === "pending" ? "pending" : "";
});

const {
  rows,
  isLoading,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useAdminLeadQueue({
  status: queueStatus,
  needsAttention: computed(() => (activeTab.value === "attention" ? attentionScope.value : undefined)),
  attentionReason,
  enabled: computed(() => activeTab.value === "pending" || activeTab.value === "all" || activeTab.value === "attention"),
  limit: 20,
});

const draftStatusFilter = computed<"pending" | "submitted" | "all">(() => {
  if (activeTab.value === "draft-pending") return "pending";
  if (activeTab.value === "draft-all") return "all";
  return "pending";
});

const {
  draftLeads,
  isLoading: isDraftLoading,
  error: draftError,
  hasNextPage: hasDraftNextPage,
  fetchNextPage: fetchNextDraftPage,
  isFetchingNextPage: isDraftFetchingNextPage,
  refetch: refetchDraftLeads,
} = useAdminDraftLeads({ status: draftStatusFilter });

const { mutateAsync: submitDraft } = useSubmitDraftLead();
const { mutateAsync: deleteDraft, isPending: isDeletingDraft } = useDeleteDraftLead();
const { mutateAsync: deleteLead, isPending: isDeleting } = useDeleteLead();
const { mutateAsync: markLeadResolved, isPending: isMarkingResolved } = useMarkLeadResolved();

const selectedLeadId = ref<number | null>(null);
const {
  lead: selectedLead,
  refetch: refetchLead,
} = useAdminLeadDetail(selectedLeadId);

const asideOpen = ref(false);
const assigningLead = ref<LeadAdminResponse | null>(null);
const assignFormRef = useTemplateRef("assignFormRef");

const formAsideOpen = ref(false);
const editingDraft = ref<DraftLeadAdminResponse | null>(null);
const editingLead = ref<LeadAdminResponse | null>(null);
const draftFormRef = useTemplateRef("draftFormRef");
const leadFormRef = useTemplateRef("leadFormRef");

const queryClient = useQueryClient();

const deleteDialogOpen = ref(false);
const leadToDelete = ref<LeadAdminResponse | null>(null);

const deleteDraftDialogOpen = ref(false);
const draftToDelete = ref<DraftLeadAdminResponse | null>(null);

const markResolvedDialogOpen = ref(false);
const leadToMarkResolved = ref<LeadAdminResponse | null>(null);

const groupedQueueRows = computed<AdminLeadQueueRow[][]>(() => {
  const map = new Map<number, AdminLeadQueueRow[]>();
  for (const row of rows.value) {
    const group = map.get(row.lead.id);
    if (group) {
      group.push(row);
    } else {
      map.set(row.lead.id, [row]);
    }
  }
  return Array.from(map.values());
});

async function loadLead(row: AdminLeadQueueRow): Promise<LeadAdminResponse | null> {
  selectedLeadId.value = row.lead.id;
  const result = await refetchLead();
  return (result.data ?? selectedLead.value) as LeadAdminResponse | null;
}

async function onAssignClick(row: AdminLeadQueueRow) {
  const lead = await loadLead(row);
  if (!lead) return;
  assigningLead.value = lead;
  asideOpen.value = true;
}

function onAssignCancel() {
  assignFormRef.value?.resetForm();
  asideOpen.value = false;
  assigningLead.value = null;
}

function onAssignSuccess() {
  assignFormRef.value?.resetForm();
  queryClient.invalidateQueries({ queryKey: ["admin", "leads-projects", "queue"] })
  asideOpen.value = false;
  assigningLead.value = null;
  toaster.create({
    type: "success",
    title: "Experts Assigned",
    description: "The experts have been successfully assigned to the lead.",
  });
}

async function onDeleteClick(row: AdminLeadQueueRow) {
  const lead = await loadLead(row);
  if (!lead) return;
  leadToDelete.value = lead;
  deleteDialogOpen.value = true;
}

async function onEditLeadClick(row: AdminLeadQueueRow) {
  const lead = await loadLead(row);
  if (!lead) return;
  editingDraft.value = null;
  editingLead.value = lead;
  formAsideOpen.value = true;
}

function onLeadFormCancel() {
  leadFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingLead.value = null;
}

function onLeadFormSuccess() {
  leadFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingLead.value = null;
}

async function confirmDelete() {
  if (!leadToDelete.value) return;

  try {
    await deleteLead(leadToDelete.value.id);
    toaster.create({
      type: "success",
      title: "Lead Deleted",
      description: `The lead "${leadToDelete.value.project_title}" has been deleted.`,
    });
    deleteDialogOpen.value = false;
    leadToDelete.value = null;
  } catch (err) {
    console.error("[AdminLeadsProjectsPage] Error deleting lead:", err);
    toaster.create({
      type: "error",
      title: "Deletion Failed",
      description: "Failed to delete the lead. Please try again.",
    });
  }
}

async function onMarkResolvedClick(row: AdminLeadQueueRow) {
  const lead = await loadLead(row);
  if (!lead) return;

  if (lead.matches.length === 0) {
    toaster.create({
      type: "warning",
      title: "Cannot Mark as Resolved",
      description: "You must assign at least one expert before marking the lead as resolved.",
    });
    return;
  }

  leadToMarkResolved.value = lead;
  markResolvedDialogOpen.value = true;
}

async function confirmMarkResolved() {
  if (!leadToMarkResolved.value) return;

  const expertIds = leadToMarkResolved.value.matches.map((match) => match.expert.id);

  try {
    await markLeadResolved({
      leadId: leadToMarkResolved.value.id,
      expertIds,
    });
    toaster.create({
      type: "success",
      title: "Lead Marked as Resolved",
      description: `The lead "${leadToMarkResolved.value.project_title}" has been marked as matched/resolved.`,
    });
    markResolvedDialogOpen.value = false;
    leadToMarkResolved.value = null;
  } catch (err) {
    console.error("[AdminLeadsProjectsPage] Error marking lead as resolved:", err);
    toaster.create({
      type: "error",
      title: "Failed to Mark as Resolved",
      description: "Failed to update the lead status. Please try again.",
    });
  }
}

function onCreateDraftClick() {
  editingLead.value = null;
  editingDraft.value = null;
  formAsideOpen.value = true;
}

function onEditDraftClick(draft: DraftLeadAdminResponse) {
  editingLead.value = null;
  editingDraft.value = draft;
  formAsideOpen.value = true;
}

function onDraftFormCancel() {
  draftFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingDraft.value = null;
}

function onDraftFormSuccess() {
  draftFormRef.value?.resetForm();
  formAsideOpen.value = false;
  editingDraft.value = null;
}

async function onSubmitDraftClick(draft: DraftLeadAdminResponse) {
  if (!draft.is_complete) {
    toaster.create({
      type: "warning",
      title: "Cannot Submit",
      description: "Please complete all required fields before submitting.",
    });
    return;
  }

  try {
    await submitDraft(draft.id);
  } catch (err) {
    console.error("[AdminLeadsProjectsPage] Error submitting draft:", err);
  }
}

function onDeleteDraftClick(draft: DraftLeadAdminResponse) {
  draftToDelete.value = draft;
  deleteDraftDialogOpen.value = true;
}

async function confirmDeleteDraft() {
  if (!draftToDelete.value) return;

  try {
    await deleteDraft(draftToDelete.value.id);
    deleteDraftDialogOpen.value = false;
    draftToDelete.value = null;
  } catch (err) {
    console.error("[AdminLeadsProjectsPage] Error deleting draft:", err);
  }
}

function handleRefreshDraftLeads() {
  refetchDraftLeads();
}

const infiniteScrollSentinelRef = useTemplateRef<HTMLElement>("infiniteScrollSentinelRef");
let infiniteObserver: IntersectionObserver | null = null;

const isLeadTab = computed(() => activeTab.value === "pending" || activeTab.value === "all" || activeTab.value === "attention");
const isDraftTab = computed(() => activeTab.value === "draft-pending" || activeTab.value === "draft-all");

const shouldShowInfiniteSentinel = computed(() => {
  if (isLeadTab.value) {
    return groupedQueueRows.value.length > 0 && (hasNextPage.value || isFetchingNextPage.value);
  }

  if (isDraftTab.value) {
    return draftLeads.value.length > 0 && (hasDraftNextPage.value || isDraftFetchingNextPage.value);
  }

  return false;
});

const isLoadingMore = computed(() => {
  if (isLeadTab.value) return isFetchingNextPage.value;
  if (isDraftTab.value) return isDraftFetchingNextPage.value;
  return false;
});

function maybeFetchNextPage() {
  if (isLeadTab.value && hasNextPage.value && !isFetchingNextPage.value) {
    fetchNextPage();
    return;
  }

  if (isDraftTab.value && hasDraftNextPage.value && !isDraftFetchingNextPage.value) {
    fetchNextDraftPage();
  }
}

onMounted(() => {
  infiniteObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        maybeFetchNextPage();
      }
    },
    {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    },
  );

  if (infiniteScrollSentinelRef.value) {
    infiniteObserver.observe(infiniteScrollSentinelRef.value);
  }
});

// Restore scroll position once the list has rendered after returning from
// the detail page. Only fires once per mount.
let scrollRestored = false;
const isCurrentTabLoading = computed(() => (isLeadTab.value ? isLoading.value : isDraftLoading.value));
const currentTabHasItems = computed(() =>
  isLeadTab.value ? groupedQueueRows.value.length > 0 : draftLeads.value.length > 0,
);

watch(
  [isCurrentTabLoading, currentTabHasItems, scrollContainer],
  async ([loading, hasItems, container]) => {
    if (scrollRestored || loading || !container || !hasItems) return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    } catch {
      // ignore
    }
    if (saved === null) {
      scrollRestored = true;
      return;
    }
    const top = Number(saved);
    if (Number.isFinite(top) && top > 0) {
      await nextTick();
      container.scrollTop = top;
    }
    try {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    } catch {
      // ignore
    }
    scrollRestored = true;
  },
  { immediate: true },
);

watch(infiniteScrollSentinelRef, (newEl, oldEl) => {
  if (!infiniteObserver) return;
  if (oldEl) infiniteObserver.unobserve(oldEl);
  if (newEl) infiniteObserver.observe(newEl);
});

onUnmounted(() => {
  if (infiniteObserver && infiniteScrollSentinelRef.value) {
    infiniteObserver.unobserve(infiniteScrollSentinelRef.value);
  }
  infiniteObserver?.disconnect();
});
</script>

<template>
  <main ref="scrollContainer" class="flex-1 p-8 overflow-y-auto bg-secondary font-light w-full">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-3xl font-normal">Leads &amp; Projects Manager</h1>
        <p class="mt-1 text-gray-600">Manage and assign experts to client leads</p>
      </div>
      <UiButton variant="primary" label="Create Draft Lead" icon="plus-square" @click="onCreateDraftClick" />
    </div>

    <div class="relative">
      <div
        v-if="activeTab === 'draft-pending' || activeTab === 'draft-all'"
        class="absolute right-0 top-0 z-10"
      >
        <UiButton
          variant="ghost"
          label="Refresh"
          icon="repeat"
          :loading="false"
          @click="handleRefreshDraftLeads"
        />
      </div>

      <UiTabs v-model="activeTab" default-value="pending">
        <UiTabsItem value="pending" label="Pending Leads">
          <div v-if="isLoading" class="text-center py-12">
            <UiText variant="p" as="p" text="Loading leads..." />
          </div>

          <div v-else-if="error" class="text-center py-12">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
              <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
                Failed to load leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-red-600">
                {{ error.message || "An error occurred while fetching leads" }}
              </UiText>
            </div>
          </div>

          <div v-else-if="groupedQueueRows.length === 0" class="text-center py-12">
            <div class="flex flex-col items-center gap-4">
              <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
              <div>
                <UiText variant="h6" as="h6" class="font-semibold mb-1">
                  No pending leads
                </UiText>
                <UiText variant="p-small" as="p" class="text-gray-600">
                  All leads have been matched with experts
                </UiText>
              </div>
            </div>
          </div>

          <div v-else class="flex flex-col gap-6">
            <AdminLeadProjectCard
              v-for="rowsGroup in groupedQueueRows"
              :key="rowsGroup[0]?.lead.id"
              :rows="rowsGroup"
              @assign="onAssignClick"
              @edit="onEditLeadClick"
              @delete="onDeleteClick"
              @mark-resolved="onMarkResolvedClick"
            />
          </div>
        </UiTabsItem>

        <UiTabsItem value="all" label="All Leads">
          <div v-if="isLoading" class="text-center py-12">
            <UiText variant="p" as="p" text="Loading leads..." />
          </div>

          <div v-else-if="error" class="text-center py-12">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
              <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
                Failed to load leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-red-600">
                {{ error.message || "An error occurred while fetching leads" }}
              </UiText>
            </div>
          </div>

          <div v-else-if="groupedQueueRows.length === 0" class="text-center py-12">
            <div class="flex flex-col items-center gap-4">
              <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
              <div>
                <UiText variant="h6" as="h6" class="font-semibold mb-1">
                  No leads found
                </UiText>
                <UiText variant="p-small" as="p" class="text-gray-600">
                  There are no leads in the system yet
                </UiText>
              </div>
            </div>
          </div>

          <div v-else class="flex flex-col gap-6">
            <AdminLeadProjectCard
              v-for="rowsGroup in groupedQueueRows"
              :key="`${rowsGroup[0]?.lead.id}-all`"
              :rows="rowsGroup"
              @assign="onAssignClick"
              @edit="onEditLeadClick"
              @delete="onDeleteClick"
              @mark-resolved="onMarkResolvedClick"
            />
          </div>
        </UiTabsItem>

        <UiTabsItem value="attention" label="Needs Attention">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <UiSegmentedControl v-model="attentionReason" :options="ATTENTION_REASON_OPTIONS" class="md:w-96 shrink-0" />
              <UiSegmentedControl v-model="attentionScope" :options="ATTENTION_SCOPE_OPTIONS" class="md:w-96 shrink-0" />
            </div>
            <UiText
              as="p"
              variant="p-small"
              class="text-gray-600"
              :text="`${ATTENTION_DESCRIPTIONS[attentionReason].intro} The alert window is set in Admin Settings.`"
            />

            <div v-if="isLoading" class="text-center py-12">
              <UiText variant="p" as="p" text="Loading projects..." />
            </div>

            <div v-else-if="error" class="text-center py-12">
              <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
                <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
                  Failed to load projects
                </UiText>
                <UiText variant="p-small" as="p" class="text-red-600">
                  {{ error.message || "An error occurred while fetching projects" }}
                </UiText>
              </div>
            </div>

            <div v-else-if="groupedQueueRows.length === 0" class="text-center py-12">
              <div class="flex flex-col items-center gap-4">
                <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
                <div>
                  <UiText variant="h6" as="h6" class="font-semibold mb-1">
                    Nothing needs attention
                  </UiText>
                  <UiText variant="p-small" as="p" class="text-gray-600">
                    {{ ATTENTION_DESCRIPTIONS[attentionReason].empty }}
                  </UiText>
                </div>
              </div>
            </div>

            <div v-else class="flex flex-col gap-6">
              <AdminLeadProjectCard
                v-for="rowsGroup in groupedQueueRows"
                :key="`${rowsGroup[0]?.lead.id}-attention`"
                :rows="rowsGroup"
                @assign="onAssignClick"
                @edit="onEditLeadClick"
                @delete="onDeleteClick"
                @mark-resolved="onMarkResolvedClick"
              />
            </div>
          </div>
        </UiTabsItem>

        <UiTabsItem value="draft-pending" label="Draft Leads">
          <div v-if="isDraftLoading" class="text-center py-12">
            <UiText variant="p" as="p" text="Loading draft leads..." />
          </div>

          <div v-else-if="draftError" class="text-center py-12">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
              <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
                Failed to load draft leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-red-600">
                {{ draftError.message || "An error occurred while fetching draft leads" }}
              </UiText>
            </div>
          </div>

          <div v-else-if="draftLeads.length === 0" class="text-center py-12">
            <div class="flex flex-col items-center gap-4">
              <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
              <div>
                <UiText variant="h6" as="h6" class="font-semibold mb-1">
                  No draft leads
                </UiText>
                <UiText variant="p-small" as="p" class="text-gray-600">
                  Create a draft lead to get started
                </UiText>
              </div>
              <UiButton variant="primary" @click="onCreateDraftClick">
                <template #before_label>
                  <UiIcon name="plus-square" class="w-4 h-4" />
                </template>
                Create Draft Lead
              </UiButton>
            </div>
          </div>

          <div v-else class="flex flex-col gap-6">
            <DraftLeadCard
              v-for="draft in draftLeads"
              :key="draft.id"
              :draft-lead="draft"
              @edit="onEditDraftClick"
              @submit="onSubmitDraftClick"
              @delete="onDeleteDraftClick"
            />
          </div>
        </UiTabsItem>

        <UiTabsItem value="draft-all" label="All Draft Leads">
          <div v-if="isDraftLoading" class="text-center py-12">
            <UiText variant="p" as="p" text="Loading draft leads..." />
          </div>

          <div v-else-if="draftError" class="text-center py-12">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
              <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
                Failed to load draft leads
              </UiText>
              <UiText variant="p-small" as="p" class="text-red-600">
                {{ draftError.message || "An error occurred while fetching draft leads" }}
              </UiText>
            </div>
          </div>

          <div v-else-if="draftLeads.length === 0" class="text-center py-12">
            <div class="flex flex-col items-center gap-4">
              <UiIcon name="package-placeholder" class="w-16 h-16 text-gray-400" />
              <div>
                <UiText variant="h6" as="h6" class="font-semibold mb-1">
                  No draft leads
                </UiText>
                <UiText variant="p-small" as="p" class="text-gray-600">
                  Create a draft lead to get started
                </UiText>
              </div>
              <UiButton variant="primary" @click="onCreateDraftClick">
                <template #before_label>
                  <UiIcon name="plus-square" class="w-4 h-4" />
                </template>
                Create Draft Lead
              </UiButton>
            </div>
          </div>

          <div v-else class="flex flex-col gap-6">
            <DraftLeadCard
              v-for="draft in draftLeads"
              :key="draft.id"
              :draft-lead="draft"
              @edit="onEditDraftClick"
              @submit="onSubmitDraftClick"
              @delete="onDeleteDraftClick"
            />
          </div>
        </UiTabsItem>
      </UiTabs>

      <div
        v-if="shouldShowInfiniteSentinel"
        ref="infiniteScrollSentinelRef"
        class="h-14 flex items-center justify-center"
      >
        <UiText v-if="isLoadingMore" variant="p-small" as="p" class="text-gray-500">
          Loading more...
        </UiText>
      </div>
    </div>

    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <div class="flex items-center justify-between">
          <UiText as="h3" variant="h3">
            Assign Experts
          </UiText>
          <UiButton variant="ghost" shape="square" icon="close" @click="onAssignCancel" />
        </div>
        <UiText
          as="p"
          class="text-gray-600 mb-4"
          text="Search and select up to 3 experts to assign to this lead. The experts will be notified about the opportunity."
        />

        <AssignExpertsForm
          v-if="assigningLead"
          ref="assignFormRef"
          :lead-id="assigningLead.id"
          :lead-title="assigningLead.project_title"
          :current-matches-count="assigningLead.matches_count"
          :max-matches="assigningLead.max_matches"
          @success="onAssignSuccess"
          @cancel="onAssignCancel"
        />
      </div>
    </UiAside>

    <UiAside v-model="formAsideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <template v-if="!editingLead">
          <div class="flex items-center justify-between">
            <UiText as="h3" variant="h3">
              {{ editingDraft ? "Edit Draft Lead" : "Create Draft Lead" }}
            </UiText>
            <UiButton variant="ghost" shape="square" icon="close" @click="onDraftFormCancel" />
          </div>
          <UiText
            as="p"
            class="text-gray-600 mb-4"
            :text="editingDraft
              ? 'Update the draft lead details below.'
              : 'Create a new draft lead. You can save it and submit later, or submit immediately.'"
          />

          <DraftLeadForm
            ref="draftFormRef"
            :draft-lead="editingDraft"
            @success="onDraftFormSuccess"
            @cancel="onDraftFormCancel"
          />
        </template>

        <template v-else>
          <div class="flex items-center justify-between">
            <UiText as="h3" variant="h3">
              Edit Lead
            </UiText>
            <UiButton variant="ghost" shape="square" icon="close" @click="onLeadFormCancel" />
          </div>
          <UiText
            as="p"
            class="text-gray-600 mb-4"
            text="Update the lead details below."
          />

          <LeadForm
            ref="leadFormRef"
            :lead="editingLead"
            @success="onLeadFormSuccess"
            @cancel="onLeadFormCancel"
          />
        </template>
      </div>
    </UiAside>

    <UiDialog
      v-model="deleteDialogOpen"
      v-model:loading="isDeleting"
      title="Delete Lead"
      :text="`Are you sure you want to delete the lead &quot;${leadToDelete?.project_title}&quot;? This action cannot be undone.`"
      primary-action-label="Delete Lead"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmDelete"
      @secondary-action="deleteDialogOpen = false"
    />

    <UiDialog
      v-model="deleteDraftDialogOpen"
      v-model:loading="isDeletingDraft"
      title="Delete Draft Lead"
      :text="`Are you sure you want to delete the draft lead &quot;${draftToDelete?.project_title || 'Untitled Draft'}&quot;? This action cannot be undone.`"
      primary-action-label="Delete Draft"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmDeleteDraft"
      @secondary-action="deleteDraftDialogOpen = false"
    />

    <UiDialog
      v-model="markResolvedDialogOpen"
      v-model:loading="isMarkingResolved"
      title="Mark Lead as Resolved"
      :text="`Are you sure you want to mark the lead &quot;${leadToMarkResolved?.project_title}&quot; as resolved? This will change its status to matched.`"
      primary-action-label="Mark as Resolved"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmMarkResolved"
      @secondary-action="markResolvedDialogOpen = false"
    />
  </main>
</template>
